class MixMatch extends HTMLElement {
  constructor() {
    super();
    this._unsubs = [];
    this._limitAlertHideTimer = null;
    this._limitAlertResetTimer = null;
    this._onLimitAlertKeydown = this._onLimitAlertKeydown.bind(this);
    this._positionLimitAlert = this._positionLimitAlert.bind(this);
  }

  connectedCallback() {
    this.storage = new MixMatchStorage(window.mmConfig);
    window.mmStorage = this.storage;

    this._unsubs.push(
      subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this._refreshProductCardPrices()),
      subscribe(PUB_SUB_EVENTS.variantChange, () => this._refreshProductCardPrices()),
    );

    this._refreshProductCardPrices();
    this._initProductsSliderDrag();
    this._initLimitAlert();

    console_log('MixMatch initialized', this.storage.snapshot());
  }

  disconnectedCallback() {
    this._unsubs.forEach((u) => u?.());
    this._unsubs = [];
    this._productsSliderDragCleanup?.();
    this._productsSliderDragCleanup = null;
    this._teardownLimitAlert();
  }

  showLimitAlert() {
    const alertEl = this._limitAlert;
    if (!alertEl) return;

    const max = this.storage?.config?.max_products;
    const locale = this.storage?.config?.locale || {};
    const titleEl = alertEl.querySelector('[data-mm-limit-alert-title]');
    const messageEl = alertEl.querySelector('[data-mm-limit-alert-message]');

    if (titleEl && locale.limit_alert_title_html && max != null) {
      titleEl.textContent = locale.limit_alert_title_html.replace(/\{max\}/gi, max);
    }
    if (messageEl && locale.limit_alert_message) {
      messageEl.textContent = locale.limit_alert_message;
    }

    clearTimeout(this._limitAlertHideTimer);
    clearTimeout(this._limitAlertResetTimer);
    this._limitAlertResetTimer = null;

    this._positionLimitAlert();
    alertEl.classList.add('is-open');
    alertEl.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => this._positionLimitAlert());
    document.addEventListener('keydown', this._onLimitAlertKeydown);
    window.addEventListener('resize', this._positionLimitAlert);
    window.addEventListener('scroll', this._positionLimitAlert, true);

    this._limitAlertHideTimer = setTimeout(() => this.hideLimitAlert(), 2500);
  }

  hideLimitAlert() {
    const alertEl = this._limitAlert;
    if (!alertEl) return;

    clearTimeout(this._limitAlertHideTimer);
    this._limitAlertHideTimer = null;
    clearTimeout(this._limitAlertResetTimer);
    alertEl.classList.remove('is-open');
    alertEl.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', this._onLimitAlertKeydown);
    window.removeEventListener('resize', this._positionLimitAlert);
    window.removeEventListener('scroll', this._positionLimitAlert, true);
    // Keep the last desktop coordinates until the fade finishes, otherwise
    // the card jumps to the top of the viewport while still visible.
    this._limitAlertResetTimer = setTimeout(() => {
      this._limitAlertResetTimer = null;
      this._resetLimitAlertPosition();
    }, 250);
  }

  _initLimitAlert() {
    this._limitAlert = this.querySelector('[data-mm-limit-alert]');
    if (!this._limitAlert) return;

    this._limitAlertCard = this._limitAlert.querySelector('[data-mm-limit-alert-card]');
    this._limitAlert.addEventListener('click', (event) => {
      if (event.target.closest('[data-mm-limit-alert-dismiss], [data-mm-limit-alert-card]')) {
        this.hideLimitAlert();
      }
    });
  }

  _positionLimitAlert() {
    const card = this._limitAlertCard;
    if (!card) return;

    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    if (!isDesktop) {
      this._resetLimitAlertPosition();
      return;
    }

    const anchor = this.querySelector('selected-products-slider');
    if (!anchor) {
      this._resetLimitAlertPosition();
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const gap = 12;
    // Temporarily clear top so height measurement is accurate.
    card.style.left = `${rect.left}px`;
    card.style.width = `${rect.width}px`;
    card.style.right = 'auto';
    card.style.maxWidth = 'none';
    card.style.transform = 'none';
    const cardHeight = card.offsetHeight || 0;
    card.style.top = `${Math.max(12, rect.top - cardHeight - gap)}px`;
  }

  _resetLimitAlertPosition() {
    const card = this._limitAlertCard;
    if (!card) return;
    card.style.left = '';
    card.style.top = '';
    card.style.width = '';
    card.style.right = '';
    card.style.maxWidth = '';
    card.style.transform = '';
  }

  _teardownLimitAlert() {
    clearTimeout(this._limitAlertHideTimer);
    this._limitAlertHideTimer = null;
    clearTimeout(this._limitAlertResetTimer);
    this._limitAlertResetTimer = null;
    document.removeEventListener('keydown', this._onLimitAlertKeydown);
    window.removeEventListener('resize', this._positionLimitAlert);
    window.removeEventListener('scroll', this._positionLimitAlert, true);
    this._resetLimitAlertPosition();
    this._limitAlert = null;
    this._limitAlertCard = null;
  }

  _onLimitAlertKeydown(event) {
    if (event.key === 'Escape') this.hideLimitAlert();
  }

  // Desktop pointer only: mobile/tablet should keep native page scrolling.
  _initProductsSliderDrag() {
    const el = this.querySelector('.products__slider');
    if (!el) return;

    const mq = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');
    let teardown = null;

    const bind = () => {
      teardown?.();
      teardown = null;
      if (!mq.matches) return;

      let drag = false;
      let startX = 0;
      let startScroll = 0;

      const interactive = (t) => t.closest('button, a, input, textarea, select, label, qty-picker');

      const down = (e) => {
        if (e.button !== 0 || interactive(e.target)) return;
        drag = true;
        startX = e.clientX;
        startScroll = el.scrollLeft;
        el.classList.add('is-dragging');
      };

      const move = (e) => {
        if (!drag) return;
        el.scrollLeft = startScroll - (e.clientX - startX);
      };

      const up = () => {
        if (!drag) return;
        drag = false;
        el.classList.remove('is-dragging');
      };

      el.addEventListener('mousedown', down);
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', up);

      teardown = () => {
        el.removeEventListener('mousedown', down);
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', up);
        up();
      };
    };

    bind();
    mq.addEventListener('change', bind);

    this._productsSliderDragCleanup = () => {
      mq.removeEventListener('change', bind);
      teardown?.();
    };
  }

  _refreshProductCardPrices() {
    const storage = this.storage;
    if (!storage) return;

    this.querySelectorAll('.product__card[data-product-handle]').forEach((card) => {
      const handle = card.dataset.productHandle;
      const pricing = storage.getProductCardPricing(handle);
      if (!pricing) return;

      const priceEl = card.querySelector('.variant-price');
      const discEl = card.querySelector('.variant-price-discounted');
      const badgeEl = card.querySelector('.discount-percentage');
      if (!priceEl || !discEl || !badgeEl) return;

      if (pricing.discountPercentage > 0) {
        priceEl.textContent = _formatPrice(pricing.unitPriceCents);
        priceEl.classList.add('variant-price--compare');
        discEl.textContent = _formatPrice(pricing.unitDiscountedCents);
        discEl.hidden = false;
        badgeEl.textContent = `-${pricing.discountPercentage}%`;
        badgeEl.hidden = false;
      } else {
        priceEl.textContent = _formatPrice(pricing.unitPriceCents);
        priceEl.classList.remove('variant-price--compare');
        discEl.textContent = '';
        discEl.hidden = true;
        badgeEl.textContent = '';
        badgeEl.hidden = true;
      }
    });
  }
}

if (!customElements.get('mix-match')) {
  customElements.define('mix-match', MixMatch);
}
